/*eslint complexity: ["error", 30]*/
import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GetProjectDto,
  GetStageDefinitionWithStatusDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import momencik from '../../../common/momencik';
import { BadCompany } from '../../../common/components/NotAuthorized/BadCompany';
import { useAuth } from '../../../common/hooks/useAuth';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import ClientInfo from '../../components/Clients/ClientInfo';
import { ProjectTabs } from '../../components/Projects/ProjectTabs';
import { TimeLine } from '../../components/TimeLine/TimeLine';
import CopyButton from '../../../common/components/CopyButton/CopyButton';
import { teamName } from '../../../utils/teamName';

type ParamTypes = {
  projectId: string;
};

export const ProjectDetailsView: React.FC = () => {
  const [project, setProject] = useState<GetProjectDto>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [uniqueServices, setUniqueServices] = useState<string[]>();
  const [paeTeam, setPaeTeam] = useState<boolean>(false);
  const [stages, setStages] = useState<GetStageDefinitionWithStatusDto[]>([]);
  const [stagesLoading, setStagesLoading] = useState<boolean>(true);
  const [month, setMonth] = useState<string | undefined>();
  const [refreshTimeline, setRefreshTimeline] = useState<boolean>(false);
  const { projectId: projectId } = useParams<ParamTypes>() as {
    projectId: string;
  };
  const pok = usePokCore();
  const auth = useAuth();
  const notifications = useNotifications();
  const [authorized, setAuthorized] = useState<boolean>();
  const { t, tk } = useTranslation('projectLng');

  const showTimeLine =
    process.env.REACT_APP_TIMELINE?.toLocaleLowerCase() === 'on';

  useEffect(() => {
    if (project && project?.company?.id !== pok.getCompanyId()) {
      setAuthorized(false);
    } else {
      if (project) {
        setAuthorized(
          auth.check(PermissionsEnum.Projects, project?.company?.id),
        );
      }
    }
  }, [pok, project, auth]);

  useEffect(() => {
    pok.projects
      .getUniqueServicesNames(projectId)
      .then(setUniqueServices)
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [notifications, pok.projects, projectId, refresh]);

  useEffect(() => {
    pok.projects
      .getWithParent(projectId)
      .then(setProject)
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [notifications, pok.projects, projectId, refresh]);

  useEffect(() => {
    pok.teams
      .paeTeam()
      .then(setPaeTeam)
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [notifications, pok.teams]);

  useEffect(() => {
    const monthDate = month ? new Date(month) : undefined;
    setStagesLoading(true);
    pok.stageDefinitions
      .getStagesForProject(projectId, monthDate)
      .then(setStages)
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setStagesLoading(false));
  }, [notifications, pok.stageDefinitions, projectId, month, refreshTimeline]);

  const onRefresh = () => {
    setRefresh(!refresh);
  };

  return (
    <>
      {!authorized && project && (
        <BadCompany companyId={project?.company?.id} />
      )}
      {authorized && project && (
        <Card className="main-card">
          <Card.Body className="m-3">
            <Card.Title className="pb-3">
              <div className="side-by-side">
                <div className="left-70">
                  <p>
                    {t(tk.prLang.project)}:{' '}
                    <b>
                      {project?.name} ({project?.number})
                    </b>
                    <CopyButton
                      value={project?.number}
                      variant="link"
                      tooltipText={t(tk.projects.copyNumberToClipboard)}
                      className="ps-1 pt-1 mb-1"
                    />
                    {`${t(tk.common.sysNumberLowerCase)} `}
                    {project?.systemNumber}
                  </p>
                  {project?.parent && (
                    <p>
                      <small>
                        {t(tk.prLang.internalProject)}: {project?.parent?.name}{' '}
                        ({project?.parent?.number})
                      </small>
                    </p>
                  )}
                  <p className="font-90">
                    {`${t(tk.projects.for)}: `}
                    {project?.purchaser.name} (
                    {project?.purchaser?.client?.name})
                    <ClientInfo
                      id={project?.purchaser?.client.id}
                      className="color-primary ms-2 mb-1"
                      iconSize={18}
                      placement="right"
                    />
                  </p>
                </div>
                <div className="right-30">
                  <p>
                    {`${t(tk.projects.from)}: `}
                    <b>{momencik(project?.startDate, 'L')}</b>{' '}
                    {`${t(tk.projects.to)}: `}
                    <b>{momencik(project?.endDate, 'L')}</b>
                  </p>
                  <p className="font-90">
                    {`${t(tk.common.team)}: `}
                    {project?.projectTeams
                      ?.map(t => teamName(t.team))
                      .join(', ')}
                  </p>
                </div>
              </div>
              <div>
                {showTimeLine && (
                  <TimeLine
                    stages={stages}
                    startDate={project.startDate}
                    endDate={project.endDate}
                    onMonthChange={setMonth}
                    projectId={projectId}
                    isLoading={stagesLoading}
                    onRefresh={() => setRefreshTimeline(!refreshTimeline)}
                  />
                )}
              </div>
            </Card.Title>
            {project && (
              <ProjectTabs
                project={project}
                uniqueServices={uniqueServices}
                paeTeam={paeTeam}
                onRefresh={onRefresh}
              />
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};
